/**
 * The wind and percussion presets: woodwind, brass, then tuned and untuned percussion.
 *
 * Split from the strings and keyboards only because one table of every instrument is longer
 * than a file should be. The order within it is score order, which is the order a picker
 * should list them in.
 */

import { BASS, TREBLE } from "../document/types";
import { AT_PITCH } from "../document/pitch";
import type { Transposition } from "../document/pitch";
import type { InstrumentPreset } from "./instruments";

const octave = (octaveChange: number): Transposition => ({
  chromatic: 0,
  diatonic: 0,
  octaveChange,
});
/** A transposing instrument, named by how far its written pitch stands above its sound. */
const down = (diatonic: number, chromatic: number, octaveChange = 0): Transposition => ({
  chromatic,
  diatonic,
  octaveChange,
});

const IN_B_FLAT = down(-1, -2);
const IN_A = down(-2, -3);
const IN_F = down(-4, -7);
const IN_E_FLAT = down(-5, -9);

/** Every instrument a part can be made from, in score order within each family. */

export const WIND_AND_PERCUSSION: readonly InstrumentPreset[] = [
  {
    id: "piccolo",
    name: "Piccolo",
    abbreviation: "Picc.",
    family: "woodwind",
    clefs: [TREBLE],
    transposition: octave(1),
    sound: "wind.flutes.flute.piccolo",
    midiProgram: 72,
    range: { low: 62, high: 96 },
  },
  {
    id: "flute",
    name: "Flute",
    abbreviation: "Fl.",
    family: "woodwind",
    clefs: [TREBLE],
    transposition: AT_PITCH,
    sound: "wind.flutes.flute",
    midiProgram: 73,
    range: { low: 59, high: 96 },
  },
  {
    id: "oboe",
    name: "Oboe",
    abbreviation: "Ob.",
    family: "woodwind",
    clefs: [TREBLE],
    transposition: AT_PITCH,
    sound: "wind.reed.oboe",
    midiProgram: 68,
    range: { low: 58, high: 89 },
  },
  {
    id: "cor-anglais",
    name: "Cor anglais",
    abbreviation: "C.a.",
    family: "woodwind",
    clefs: [TREBLE],
    transposition: IN_F,
    sound: "wind.reed.english-horn",
    midiProgram: 69,
    range: { low: 59, high: 86 },
  },
  {
    id: "clarinet-bb",
    name: "Clarinet in B♭",
    abbreviation: "Cl.",
    family: "woodwind",
    clefs: [TREBLE],
    transposition: IN_B_FLAT,
    sound: "wind.reed.clarinet.bflat",
    midiProgram: 71,
    range: { low: 50, high: 91 },
  },
  {
    id: "clarinet-a",
    name: "Clarinet in A",
    abbreviation: "Cl.",
    family: "woodwind",
    clefs: [TREBLE],
    transposition: IN_A,
    sound: "wind.reed.clarinet.a",
    midiProgram: 71,
    range: { low: 50, high: 91 },
  },
  {
    id: "bass-clarinet",
    name: "Bass clarinet",
    abbreviation: "B. cl.",
    family: "woodwind",
    clefs: [TREBLE],
    transposition: down(-1, -2, -1),
    sound: "wind.reed.clarinet.bass",
    midiProgram: 71,
    range: { low: 50, high: 86 },
  },
  {
    id: "bassoon",
    name: "Bassoon",
    abbreviation: "Bsn.",
    family: "woodwind",
    clefs: [BASS],
    transposition: AT_PITCH,
    sound: "wind.reed.bassoon",
    midiProgram: 70,
    range: { low: 34, high: 75 },
  },
  {
    id: "contrabassoon",
    name: "Contrabassoon",
    abbreviation: "Cbsn.",
    family: "woodwind",
    clefs: [BASS],
    transposition: octave(-1),
    sound: "wind.reed.contrabassoon",
    midiProgram: 70,
    range: { low: 34, high: 69 },
  },
  {
    id: "soprano-sax",
    name: "Soprano saxophone",
    abbreviation: "S. sax.",
    family: "woodwind",
    clefs: [TREBLE],
    transposition: IN_B_FLAT,
    sound: "wind.reed.saxophone.soprano",
    midiProgram: 64,
    range: { low: 56, high: 87 },
  },
  {
    id: "alto-sax",
    name: "Alto saxophone",
    abbreviation: "A. sax.",
    family: "woodwind",
    clefs: [TREBLE],
    transposition: IN_E_FLAT,
    sound: "wind.reed.saxophone.alto",
    midiProgram: 65,
    range: { low: 56, high: 87 },
  },
  {
    id: "tenor-sax",
    name: "Tenor saxophone",
    abbreviation: "T. sax.",
    family: "woodwind",
    clefs: [TREBLE],
    transposition: down(-1, -2, -1),
    sound: "wind.reed.saxophone.tenor",
    midiProgram: 66,
    range: { low: 56, high: 87 },
  },
  {
    id: "baritone-sax",
    name: "Baritone saxophone",
    abbreviation: "B. sax.",
    family: "woodwind",
    clefs: [TREBLE],
    transposition: down(-5, -9, -1),
    sound: "wind.reed.saxophone.baritone",
    midiProgram: 67,
    range: { low: 56, high: 87 },
  },

  {
    id: "horn",
    name: "Horn in F",
    abbreviation: "Hn.",
    family: "brass",
    clefs: [TREBLE],
    transposition: IN_F,
    sound: "brass.french-horn",
    midiProgram: 60,
    range: { low: 41, high: 84 },
  },
  {
    id: "trumpet-bb",
    name: "Trumpet in B♭",
    abbreviation: "Tpt.",
    family: "brass",
    clefs: [TREBLE],
    transposition: IN_B_FLAT,
    sound: "brass.trumpet.bflat",
    midiProgram: 56,
    range: { low: 54, high: 84 },
  },
  {
    id: "trumpet-c",
    name: "Trumpet in C",
    abbreviation: "Tpt.",
    family: "brass",
    clefs: [TREBLE],
    transposition: AT_PITCH,
    sound: "brass.trumpet.c",
    midiProgram: 56,
    range: { low: 52, high: 82 },
  },
  {
    id: "trombone",
    name: "Trombone",
    abbreviation: "Tbn.",
    family: "brass",
    clefs: [BASS],
    transposition: AT_PITCH,
    sound: "brass.trombone",
    midiProgram: 57,
    range: { low: 40, high: 77 },
  },
  {
    id: "bass-trombone",
    name: "Bass trombone",
    abbreviation: "B. tbn.",
    family: "brass",
    clefs: [BASS],
    transposition: AT_PITCH,
    sound: "brass.trombone.bass",
    midiProgram: 57,
    range: { low: 34, high: 72 },
  },
  {
    id: "tuba",
    name: "Tuba",
    abbreviation: "Tba.",
    family: "brass",
    clefs: [BASS],
    transposition: AT_PITCH,
    sound: "brass.tuba",
    midiProgram: 58,
    range: { low: 26, high: 65 },
  },
  {
    id: "euphonium",
    name: "Euphonium",
    abbreviation: "Euph.",
    family: "brass",
    clefs: [BASS],
    transposition: AT_PITCH,
    sound: "brass.euphonium",
    midiProgram: 58,
    range: { low: 34, high: 75 },
  },

  {
    id: "timpani",
    name: "Timpani",
    abbreviation: "Timp.",
    family: "percussion",
    clefs: [BASS],
    transposition: AT_PITCH,
    sound: "drum.timpani",
    midiProgram: 47,
    range: { low: 36, high: 60 },
  },
  {
    id: "glockenspiel",
    name: "Glockenspiel",
    abbreviation: "Glock.",
    family: "percussion",
    clefs: [TREBLE],
    transposition: octave(2),
    sound: "pitched-percussion.glockenspiel",
    midiProgram: 9,
    range: { low: 67, high: 96 },
  },
  {
    id: "xylophone",
    name: "Xylophone",
    abbreviation: "Xyl.",
    family: "percussion",
    clefs: [TREBLE],
    transposition: octave(1),
    sound: "pitched-percussion.xylophone",
    midiProgram: 13,
    range: { low: 65, high: 96 },
  },
  {
    id: "vibraphone",
    name: "Vibraphone",
    abbreviation: "Vib.",
    family: "percussion",
    clefs: [TREBLE],
    transposition: AT_PITCH,
    sound: "pitched-percussion.vibraphone",
    midiProgram: 11,
    range: { low: 53, high: 89 },
  },
  {
    id: "marimba",
    name: "Marimba",
    abbreviation: "Mar.",
    family: "percussion",
    clefs: [TREBLE, BASS],
    transposition: AT_PITCH,
    sound: "pitched-percussion.marimba",
    midiProgram: 12,
    range: { low: 45, high: 96 },
  },
];
